/**
 * Creator Studio — Core Type Definitions
 *
 * These types define the data structures for the ADA-compliant hero banner
 * system, including the dual-output export model (background JPEG + .js overlay),
 * element tagging (STATIC/DYNAMIC, BACKGROUND_LAYER/OVERLAY_LAYER), and
 * integration points with Art Director and School Management modules.
 */

// ═══════════════════════════════════════
// Platform Types
// ═══════════════════════════════════════

export type PlatformRole = 'platform_owner' | 'super_admin' | 'admin' | 'member';

// ═══════════════════════════════════════
// Element Layer System
// ═══════════════════════════════════════

/**
 * Which layer an element belongs to for dual-output export:
 * - BACKGROUND_LAYER: Rendered into the background JPEG (visual-only, no text)
 * - OVERLAY_LAYER: Rendered into the .js overlay file (real DOM text for ADA compliance)
 */
export type ElementLayer = 'BACKGROUND_LAYER' | 'OVERLAY_LAYER';

/**
 * Whether an element's content is fixed or driven by brand data:
 * - STATIC: Same content across all schools (e.g., generic CTA text, decorative shapes)
 * - DYNAMIC: Content mapped to brand/school data (e.g., school logo, school colors, dates)
 */
export type ElementContentType = 'STATIC' | 'DYNAMIC';

// ═══════════════════════════════════════
// Visual Effect Types
// ═══════════════════════════════════════

export interface GradientStop {
  offset: number;
  color: string;
}

export interface GradientConfig {
  type: 'linear' | 'radial';
  angle: number;
  stops: GradientStop[];
}

export interface ShadowConfig {
  enabled: boolean;
  color: string;
  offsetX: number;
  offsetY: number;
  blur: number;
}

export interface FillConfig {
  type: 'solid' | 'gradient';
  color: string;
  gradient?: GradientConfig;
}

export interface TextShadowConfig {
  enabled: boolean;
  color: string;
  offsetX: number;
  offsetY: number;
  blur: number;
}

export interface BorderConfig {
  color: string;
  width: number;
  style: 'solid' | 'dashed' | 'dotted';
  radius?: {
    topLeft: number;
    topRight: number;
    bottomRight: number;
    bottomLeft: number;
    linked: boolean;
  };
}

// ═══════════════════════════════════════
// Canvas Element Types
// ═══════════════════════════════════════

export interface BaseElement {
  id: string;
  type: CanvasElementType;
  layer: ElementLayer;
  contentType: ElementContentType;

  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;

  visible: boolean;
  locked: boolean;
  zIndex: number;
  name: string;

  opacity: number;
  shadow?: ShadowConfig;
  blendMode?: string;
}

export interface TextElement extends BaseElement {
  type: 'text';
  layer: 'OVERLAY_LAYER';

  text: string;

  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  /**
   * Points at the exact cs_fonts row backing this element's rendered face.
   * Optional for backward-compat: legacy elements resolve on project open.
   * When present, `fontFamily`, `fontWeight`, and `fontStyle` are derived
   * from the variant and written in lockstep with it.
   */
  fontVariantId?: string;
  textDecoration: 'none' | 'underline' | 'line-through';
  textAlign: 'left' | 'center' | 'right';
  lineHeight: number;
  letterSpacing: number;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';

  color: string;

  textFill?: FillConfig;
  textShadow?: TextShadowConfig;
  strokeColor?: string;
  textStrokeWidth?: number;

  pathData?: string;
  pathStartOffset?: number;
  pathSide?: 'left' | 'right';
  pathAlign?: 'baseline' | 'center' | 'ascender' | 'descender';

  textPath?: TextPathConfig;

  svgFontSource?: string;

  /**
   * Tracks whether the box width is driven by content or by the user.
   *   'auto'   - width adapts to rendered text (re-measures on font change, text change, font-load settle).
   *              Never shows dead space; never clips.
   *   'manual' - user has resized the box. Width is respected but floored to the rendered minimum
   *              so text can't be clipped. No silent shrinking.
   *   undefined - legacy value. Treated as 'manual' at runtime so existing projects look unchanged,
   *               except the floor is enforced (fixes clipping caused by stale under-width).
   */
  widthIntent?: 'auto' | 'manual';

  /**
   * Governs how the text box reacts to content changes (e.g., dynamic mappings).
   *   'natural'                  - textbox grows or wraps per Fabric.js native behavior (default).
   *   'contain-within-textbox'   - shrink-then-wrap at the designer-fixed textbox dimensions.
   *                                Floors at MIN_TEXT_FONT_SIZE_PX (8px). Per Spec Lock §4.
   *   'contain-within-structure' - delimiter-based line splitting; no font shrinking.
   *                                Phase 3 (Build Prompt D) implements behavior. Per Spec Lock §5.
   * When undefined, treated as 'natural' to keep legacy behavior unchanged.
   * See cs-cwt-cws-spec-lock §1 in otto_docs for full vocabulary spec.
   */
  textFitMode?: 'natural' | 'contain-within-textbox' | 'contain-within-structure';

  /**
   * Snapshot of line count at the moment 'commit' was chosen. Used by the
   * fitter to decide when to shrink rather than reflow.
   */
  committedLineCount?: number;

  dynamicField?: DynamicFieldMapping;
}

export interface TextPathPreset {
  id: string;
  name: string;
  icon: string;
  pathData: string;
  category: 'arc' | 'wave' | 'circle';
}

export interface TextPathConfig {
  pathElementId: string;
  startOffset: number;
  side: 'left' | 'right';
  align: 'baseline' | 'center' | 'ascender' | 'descender';
}

export interface ShapeElement extends BaseElement {
  type: 'shape';
  shapeType:
    | 'rectangle'
    | 'circle'
    | 'rounded-rect'
    | 'line'
    | 'polygon'
    | 'triangle'
    | 'pentagon'
    | 'hexagon'
    | 'octagon'
    | 'star-5'
    | 'star-6'
    | 'star-8'
    | 'arrow-right'
    | 'arrow-left'
    | 'arrow-up'
    | 'arrow-down'
    | 'arrow-double'
    | 'callout'
    | 'banner';

  fill: string;
  fillOpacity: number;

  stroke: string;
  strokeWidth: number;

  borderRadius: number;

  shapeFill?: FillConfig;
  border?: BorderConfig;
  blur?: number;

  clipPath?: string;

  dynamicField?: DynamicFieldMapping;
}

export interface ImageElement extends BaseElement {
  type: 'image';

  src: string;
  alt: string;

  objectFit: 'cover' | 'contain' | 'fill' | 'none';

  blur?: number;
  brightness?: number;
  contrast?: number;
  saturation?: number;

  assetId?: string;

  dynamicField?: DynamicFieldMapping;
}

export interface ButtonElement extends BaseElement {
  type: 'button';
  layer: 'OVERLAY_LAYER';

  text: string;
  href?: string;

  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fontStyle?: 'normal' | 'italic';
  fontVariantId?: string;
  color: string;

  backgroundColor: string;
  borderRadius: number;
  paddingX: number;
  paddingY: number;

  buttonFill?: FillConfig;
  border?: BorderConfig;
  textShadow?: TextShadowConfig;
  buttonLetterSpacing?: number;

  svgFontSource?: string;

  dynamicField?: DynamicFieldMapping;
}

export interface GroupElement extends BaseElement {
  type: 'group';
  childIds: string[];
}

export type ArrowheadStyle = 'none' | 'triangle' | 'diamond' | 'circle' | 'open';

export interface LineElement extends BaseElement {
  type: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  stroke: string;
  strokeWidth: number;
  strokeDashStyle: 'solid' | 'dashed' | 'dotted' | 'dash-dot';
  arrowStart: ArrowheadStyle;
  arrowEnd: ArrowheadStyle;
  lineCap: 'butt' | 'round' | 'square';
}

export interface PathAnchor {
  x: number;
  y: number;
  handleIn?: { x: number; y: number };
  handleOut?: { x: number; y: number };
}

export interface PathElement extends BaseElement {
  type: 'path';
  pathData: string;
  anchors: PathAnchor[];
  closed: boolean;
  stroke: string;
  strokeWidth: number;
  strokeDashStyle: 'solid' | 'dashed' | 'dotted' | 'dash-dot';
  lineCap: 'butt' | 'round' | 'square';
  lineJoin: 'miter' | 'round' | 'bevel';
  fill: string;
  fillOpacity: number;
  dynamicField?: DynamicFieldMapping;
}

export interface SvgElement extends BaseElement {
  type: 'svg';
  svgMarkup: string;
  originalWidth: number;
  originalHeight: number;
  preserveAspectRatio: boolean;
  sourceFileName?: string;
  assetId?: string;
  alt?: string;
  dynamicField?: DynamicFieldMapping;
}

export type CanvasElement = TextElement | ShapeElement | ImageElement | ButtonElement | GroupElement | LineElement | PathElement | SvgElement;

export type CanvasElementType = 'text' | 'shape' | 'image' | 'button' | 'group' | 'line' | 'path' | 'svg';

// ═══════════════════════════════════════
// Brand Color Slots (Foundation)
// ═══════════════════════════════════════

export interface BrandColorSlot {
  slotId: string;
  label: string;
  currentHex: string;
  brandField?: string;
  isMapped: boolean;
}

export interface ElementColorMapping {
  elementId: string;
  colorMap: Record<string, string>;
}

// ═══════════════════════════════════════
// Dynamic Field Mapping
// ═══════════════════════════════════════

export interface DynamicFieldMapping {
  source: 'ad' | 'sm';
  table: string;
  field: string;
  targetProperty: string;
  fallbackValue?: string;
  transform?: string;
}

// ═══════════════════════════════════════
// Canvas / Template State
// ═══════════════════════════════════════

export interface CanvasDimensions {
  width: number;
  height: number;
}

export const HERO_BANNER_PRESETS: Record<string, CanvasDimensions> = {
  'hero-standard':       { width: 1140, height: 410 },
  'hero-wide':           { width: 1600, height: 500 },
  'hero-square':         { width: 800,  height: 800 },
  'email-header':        { width: 600,  height: 200 },
  'social-facebook':     { width: 1200, height: 630 },
  'social-instagram':    { width: 1080, height: 1080 },
  'display-leaderboard': { width: 728,  height: 90 },
  'display-rectangle':   { width: 300,  height: 250 },
};

export interface CanvasState {
  dimensions: CanvasDimensions;
  backgroundColor: string;
  elements: CanvasElement[];
  /**
   * Accessible reading order — ordered element IDs for text-bearing elements
   * (type 'text' | 'button') used by the accessibility overlay export.
   * Optional and additive: legacy projects have no key; runtime derives a
   * default order and reconciles on demand (see src/lib/readingOrder.ts).
   * Persisted inside canvas_state JSONB — no migration required.
   * Epic: cwt-containment-accessible-reading-order (el-ax-1).
   */
  readingOrder?: string[];
}

// ═══════════════════════════════════════
// Project & Template Types
// ═══════════════════════════════════════

export type ProjectStatus = 'draft' | 'in_progress' | 'review' | 'approved' | 'published' | 'archived';

export interface Project {
  id: string;
  name: string;
  description: string;
  status: ProjectStatus;
  canvas_state: CanvasState;
  thumbnail_url: string | null;
  preset_key: string | null;
  tags: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Template {
  id: string;
  project_id: string;
  name: string;
  description: string;
  canvas_state: CanvasState;
  dynamic_fields: DynamicFieldMapping[];
  is_published: boolean;
  version: number;
  thumbnail_url: string | null;
  category: TemplateCategory | null;
  tags: string[];
  usage_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ═══════════════════════════════════════
// Export Types (Dual-Output Packets)
// ═══════════════════════════════════════

/**
 * Accessible text payload for the export packet's heroConfig.
 * Ordered, resolved plain-text strings — one per visible text-bearing
 * element, in the project's persisted reading order. Additive-only
 * amendment to heroConfig: absent on legacy packets, and downstream
 * importers of [{ heroConfig, layers }] must not break when present.
 * Population: el-ax-1 AX1b (static text), el-ax-3 (brand-resolved values).
 */
export type AccessibleTextPayload = string[];

export type ExportStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type ExportImageFormat = 'jpeg' | 'png' | 'webp';

export interface ExportJob {
  id: string;
  template_id: string;
  status: ExportStatus;
  image_format: ExportImageFormat;
  image_quality: number;

  school_ids: string[];

  total_schools: number;
  completed_schools: number;
  failed_schools: number;

  output_files: ExportOutputFile[];

  started_at: string | null;
  completed_at: string | null;
  created_by: string;
  created_at: string;
}

export interface ExportOutputFile {
  school_id: string;
  school_name: string;
  background_url: string | null;
  overlay_url: string | null;
  status: 'pending' | 'completed' | 'failed';
  error?: string;
}

// ═══════════════════════════════════════
// Art Director Integration Types
// ═══════════════════════════════════════

export interface SchoolBrand {
  id: string;
  school_id: string;
  school_name: string;
  subdomain: string;
  solution: string;
  tier: string;
  is_active: boolean;
  email: string;

  rgb_primary: string;
  rgb_secondary: string;
  rgb_tertiary: string;
  rgb_quaternary: string;
  cmyk_primary: string;
  cmyk_secondary: string;
  cmyk_tertiary: string;
  cmyk_quaternary: string;

  mascot: string;
  storefront_url: string;
  helpdesk_url: string;
  full_promo_text: string;
  script_headline: string;
  block_variable: string;
  format_options: string;
  sale_code: string;

  has_rentals: boolean;
  has_marketplace: boolean;
  has_price_match: boolean;
  price_match_copy: string;
  price_match_seal_url: string;

  campus_store_name: string;
  campus_store_street_address: string;
  campus_store_city_state_zip: string;
  campus_store_building: string;

  marq_creator_user_id: string;
  marq_destination_folder_id: string;
  google_drive_folder_id: string;
  create_projects_in_marq: boolean;

  buyback_dates: string;
  buyback_days_of_week: string;
  buyback_times: string;
  buyback_location: string;
  rental_return_due_date: string;
  launch_date: string;

  metadata: Record<string, unknown>;
}

export interface BrandAsset {
  id: string;
  school_brand_id: string;
  category: string;
  name: string;
  file_url: string;
  file_type: string;
  metadata: Record<string, unknown>;
}

// ═══════════════════════════════════════
// School Management Integration Types
// ═══════════════════════════════════════

export interface School {
  id: string;
  name: string;
  short_name: string | null;
  legacy_id: string | null;
  city: string | null;
  state: string | null;
  region: string | null;
  mascot: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  logo_url: string | null;
  website_url: string | null;
  storefront_url: string | null;
  enrollment: number | null;
  address: string | null;
  zip_code: string | null;
  team_email: string | null;
  is_active: boolean;
  metadata: Record<string, unknown>;
}

export interface SchoolAttribute {
  id: string;
  school_id: string;
  field_key: string;
  field_value: string;
}

// ═══════════════════════════════════════
// Guide Lines
// ═══════════════════════════════════════

export type GuideOrientation = 'horizontal' | 'vertical';

export interface GuideLine {
  id: string;
  orientation: GuideOrientation;
  position: number;
  locked: boolean;
  color: string;
}

// ═══════════════════════════════════════
// Artboard Presets
// ═══════════════════════════════════════

export interface ArtboardPreset {
  id: string;
  name: string;
  category: 'social' | 'web' | 'print' | 'email' | 'custom';
  width: number;
  height: number;
  description?: string;
}

export type RulerUnit = 'px' | 'in' | 'cm' | 'mm';

// ═══════════════════════════════════════
// Asset Library
// ═══════════════════════════════════════

export type AssetCategory = 'logo' | 'icon' | 'photo' | 'illustration' | 'pattern' | 'uncategorized';

export const ASSET_CATEGORIES: { id: AssetCategory; label: string }[] = [
  { id: 'logo', label: 'Logos' },
  { id: 'icon', label: 'Icons' },
  { id: 'photo', label: 'Photos' },
  { id: 'illustration', label: 'Illustrations' },
  { id: 'pattern', label: 'Patterns' },
  { id: 'uncategorized', label: 'Uncategorized' },
];

export type AssetType = 'image' | 'svg' | 'icon';

export interface AssetRecord {
  id: string;
  name: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  thumbnail_url: string | null;
  category: string;
  tags: string[];
  width: number | null;
  height: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;

  asset_type: AssetType;
  svg_markup: string | null;
  original_file_size: number | null;
  compressed: boolean;
  folder_id: string | null;
  is_favorite: boolean;
  usage_count: number;
  blurhash: string | null;
}

export interface AssetFolder {
  id: string;
  name: string;
  parent_id: string | null;
  color: string;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ═══════════════════════════════════════
// Template Categories (Enhanced)
// ═══════════════════════════════════════

export type TemplateCategory = 'social' | 'web' | 'print' | 'email' | 'marketing' | 'custom';

export const TEMPLATE_CATEGORIES: { id: TemplateCategory; label: string }[] = [
  { id: 'social', label: 'Social Media' },
  { id: 'web', label: 'Web Banners' },
  { id: 'print', label: 'Print' },
  { id: 'email', label: 'Email' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'custom', label: 'Custom' },
];
